import { createSlice, original } from '@reduxjs/toolkit';
import { handleColumnPasteMedia, handleComponentPasteMedia } from 'service/utils/checkMediaCount';
import { v4 as uuid } from 'uuid';
import {
  addComponent,
  createSectionLayout,
  deletableMedia,
} from './reducerHelper';
import { styleHelper } from './styleHelper';

const initialState = {
  name: '',
  generation: '1st',
  section: {
    ids: [],
    entries: {},
  },
  column: {},
  component: {},
  selected: null,
  selectedType: '',
  incrementIds: [],
  decrementIds: [],
  copyableColumn: null,
  webPageBuilderPreview: false,
  isPageUpdated: false,
  selectedPageVersion: null,
  sectionIdOfSelectedColumn: null,
  breakPointMode: {
    name: 'Mobile',
    value: 'mobile',
    breakValue: 450,
    breakPoint: 'base',
    icon: 'AiOutlineMobile',
  },
  importedPageTemplateIds: [],
};

export const pageBuilderSlice = createSlice({
  name: 'builder',
  initialState,
  reducers: {
    updateState: (state, action) => {
      const sectionPayload = action.payload.section
      const templatePayload = action.payload?.importedPageTemplateIds ?? []
      Object.assign(state, {
        section: { ids: [...new Set(sectionPayload.ids)], entries: sectionPayload.entries },
        column: action.payload.column,
        component: action.payload.component,
        name: action.payload.name,
        selected: null,
        selectedType: '',
        incrementIds: [],
        decrementIds: [],
        copyableColumn: null,
        webPageBuilderPreview: false,
        importedPageTemplateIds: templatePayload
      });
    },
    updateStateByTemplate: (state, action) => {
      const { content, newMediaInfoArray, templateId } = action.payload;

      const { section, column, component } = content;
      // console.log('templateId', templateId)

      state.component = { ...state.component, ...component };
      state.column = { ...state.column, ...column };
      state.section.entries = { ...state.section.entries, ...section.entries };
      state.section.ids = [...new Set([...state.section.ids, ...section.ids])]
      state.incrementIds = [...state.incrementIds, ...newMediaInfoArray]
      state.importedPageTemplateIds.push(templateId)
    },

    updateName: (state, action) => {
      state.name = action.payload;
    },

    updatePageSaveStatus: (state, action) => {
      state.isPageUpdated = action.payload;
    },

    updateSelectedPageVersion: (state, action) => {
      state.selectedPageVersion = action.payload;
    },

    removeSelection: (state) => {
      state.selected = null;
      state.selectedType = '';
    },


    addSection: {
      reducer: (state, action) => {
        state.isPageUpdated = true;
        if (action.payload.index !== undefined) {
          // console.log('no index provided')

          state.section.ids.splice(
            action.payload.index,
            0,
            action.payload.section.id
          );
        } else {
          // console.log('undefined index')
          state.section.ids.push(action.payload.section.id);
        }
        state.section.entries[action.payload.section.id] =
          action.payload.section;
        action.payload.columns.forEach((col) => {
          state.column[col.id] = col;
          state.section.entries[action.payload.section.id].children.push(
            col.id
          );
        });

        state.selected = action.payload.section.id;
        state.selectedType = 'section';

        if (action.payload?.emptySection && action.payload.componentType) {
          // console.log('dropping on empty section new component')
          const component = addComponent(action.payload.componentType);
          let column = state.column[action.payload.columns[0].id].children;
          column.push(component.id);
          state.component[component.id] = component;
          state.selected = component.id;
          state.selectedType = component.type;
          state.isPageUpdated = true;

        }

        if (action.payload?.emptySection === false && action.payload.sourceColumn) {
          // console.log('dropping on empty section exist component')
          const sourceColumnID = action.payload.sourceColumn;
          const componentID = action.payload.componentType;
          const targetColumnID = action.payload.columns[0].id

          const columnChildren = state.column[sourceColumnID].children;
          state.column[sourceColumnID].children = columnChildren?.filter((a) => a !== action.payload.componentType)
          let column = state.column[targetColumnID].children;
          column.push(componentID);
          state.selected = componentID;
          state.selectedType = state.component[componentID].type ?? '';
          state.isPageUpdated = true;

        }
        // state.

      },
      prepare: ({ section, layout, index, emptySection, componentType, sourceColumn }) => {
        const columns = createSectionLayout(layout);
        return {
          payload: {
            section,
            columns,
            index,
            emptySection,
            componentType,
            sourceColumn
          },
        };
      },
    },



    deleteSection: (state, action) => {
      const { id } = action.payload;
      const stateObject = original(state);

      const deletableSection = stateObject.section.entries[id];
      const sectionEntryCopy = { ...stateObject.section.entries };

      if (deletableSection.style.bgImage.id) {
        state.decrementIds.push(deletableSection.style.bgImage.id);
      }
      delete sectionEntryCopy[id];
      const ids = stateObject.section.ids.filter(
        (sectionId) => sectionId !== id
      );

      const columnCopy = { ...stateObject.column };
      const componentCopy = { ...stateObject.component };

      const columnIds = deletableSection.children;
      columnIds.forEach((columnId) => delete columnCopy[columnId]);

      let componentIds = [];
      columnIds.forEach((colId) => {
        componentIds = [...componentIds, ...stateObject.column[colId].children];
      });

      componentIds.forEach((compId) => {
        const decIds = deletableMedia(componentCopy[compId]);
        state.decrementIds.push(...decIds);
        delete componentCopy[compId];
      });

      Object.assign(state, {
        section: { ids: ids, entries: sectionEntryCopy },
        column: columnCopy,
        component: componentCopy,
        selected: null,
        selectedType: '',
      });
    },

    // Section Reorder
    reorderSection: (state, action) => {
      state.isPageUpdated = true;

      const { result } = action.payload;
      const [removedSection] = state.section.ids.splice(result.source.index, 1);
      state.section.ids.splice(result.destination.index, 0, removedSection);
    },

    reorderColumnInSectionSettings: (state, action) => {
      state.isPageUpdated = true;

      const { result } = action.payload;

      const sectionId = result.destination.droppableId
      const section = state.section.entries[sectionId];
      const sectionChildren = section.children

      const [movedColumn] = sectionChildren.splice(result.source.index, 1);
      sectionChildren.splice(result.destination.index, 0, movedColumn);

    },


    // Component Reorder
    reorderComponent: (state, action) => {
      state.isPageUpdated = true;

      const { columnId, result } = action.payload;
      let column = state.column[columnId].children;
      const [removedComponent] = column.splice(result.source.index, 1);
      column.splice(result.destination.index, 0, removedComponent);
    },

    reorderComponentInAnyColumn: (state, action) => {
      state.isPageUpdated = true;

      const { result } = action.payload;


      if (result.source.droppableId !== result.destination.droppableId) {

        const sourceColumn = state.column[result.source.droppableId].children;
        const targetColumn = state.column[result.destination.droppableId].children;

        const componentToMove = sourceColumn.filter((a) => a !== result.draggableId);

        state.column[result.source.droppableId].children = componentToMove
        // targetColumn.push(result.draggableId)
        targetColumn.splice(result.destination.index, 0, result.draggableId);


      }


    },

    selectSection: (state, action) => {
      state.selected = action.payload;
      state.selectedType = 'section';
    },

    updateSection: (state, action) => {
      const { id, objectKey, property, value } = action.payload;
      state.isPageUpdated = true;
      if (property) {
        state.section.entries[id][objectKey][property] = value;
      } else {
        state.section.entries[id][objectKey] = value;
      }
    },

    addColumnToSection: (state, action) => {
      const { sectionId } = action.payload;
      const section = state.section.entries[sectionId].children;

      const getColumnWidth = (columnId, value) => {
        const widthValue = state.column[columnId]['style']['width'];

        const checkStyleValue = typeof widthValue == 'number' ? {} : widthValue;
        const fValue = {
          ...checkStyleValue,
          [state.breakPointMode.breakPoint]: +value.toFixed(2),
        };
        return fValue;
      }

      const column = styleHelper().initColumn(0);

      section.push(column.id)
      state.column[column.id] = column;

      for (let col = 0; col < section.length; col++) {
        state.column[section[col]]['style']['width'] = getColumnWidth(section[col], 100 / section.length);
      }

    },
    selectColumn: (state, action) => {
      const { columnId, sectionId } = action.payload;
      state.sectionIdOfSelectedColumn = sectionId;
      state.selected = columnId;
      state.selectedType = 'column';
    },

    updateColumn: (state, action) => {
      const { columnId, objectKey, property, value } = action.payload;
      state.isPageUpdated = true;
      if (property) {
        state.column[columnId][objectKey][property] = value;
      } else {
        state.column[columnId][objectKey] = value;
      }
    },


    updateColumnWidth: (state, action) => {
      state.isPageUpdated = true;

      const { columnArray, value } = action.payload;
      const updateColumnLength = columnArray.length;

      const getColumnWidth = (columnId, value) => {
        const widthValue = state.column[columnId]['style']['width'];

        const checkStyleValue = typeof widthValue == 'number' ? {} : widthValue;
        const fValue = {
          ...checkStyleValue,
          [state.breakPointMode.breakPoint]: +value.toFixed(2),
        };
        return fValue;
      }

      const distributeWidth = (value, colLength) => {

        if (value === 0) {
          return 100;
        }
        return value / colLength;
      };

      for (let col = 0; col < updateColumnLength; col++) {
        state.column[columnArray[col]]['style']['width'] = getColumnWidth(columnArray[col], distributeWidth(value, columnArray.length));
      }
    },

    // deleteColumn: (state, action) => {
    //   const { sectionId, columnId } = action.payload;
    //   const stateObject = original(state);
    //   state.isPageUpdated = true;
    //   const sectionEntryCopy = { ...stateObject.section.entries };
    //   const sectionEntry = sectionEntryCopy[sectionId];
    //   if (sectionEntry.children.length > 1) {
    //     const updatableColumns = sectionEntry.children.filter(
    //       (colId) => colId !== columnId
    //     );
    //     const numberOfColumn = updatableColumns.length;
    //     const columnCopy = { ...stateObject.column };
    //     const componentCopy = { ...stateObject.component };
    //     const deletableColumn = columnCopy[columnId];
    //     const deletableComponentIds = deletableColumn.children;
    //     let distributableWidth =
    //       parseFloat(deletableColumn.style.width) / numberOfColumn;
    //     const addableWidth = parseFloat(
    //       (parseFloat(distributableWidth.toFixed(3)) - 0.001).toFixed(3)
    //     );
    //     delete columnCopy[columnId];
    //     deletableComponentIds.forEach((compId) => {
    //       const decIds = deletableMedia(componentCopy[compId]);
    //       state.decrementIds.push(...decIds);
    //       delete componentCopy[compId];
    //     });
    //     sectionEntryCopy[sectionId] = {
    //       ...sectionEntry,
    //       children: updatableColumns,
    //     };
    //     updatableColumns.forEach((colId) => {
    //       columnCopy[colId] = {
    //         ...columnCopy[colId],
    //         style: {
    //           ...columnCopy[colId].style,
    //           width: columnCopy[colId].style.width + addableWidth,
    //         },
    //       };
    //     });
    //     Object.assign(state, {
    //       section: { ids: state.section.ids, entries: sectionEntryCopy },
    //       column: columnCopy,
    //       component: componentCopy,
    //       selected: null,
    //       selectedType: '',
    //     });
    //   }
    // },

    deleteColumn: (state, action) => {
      const { sectionId, columnId } = action.payload;
      const stateObject = original(state);
      state.isPageUpdated = true;

      const sectionEntryCopy = { ...stateObject.section.entries };
      const sectionEntry = sectionEntryCopy[sectionId];
      if (sectionEntry.children.length > 1) {
        const updatableColumns = sectionEntry.children.filter(
          (colId) => colId !== columnId
        );
        const numberOfColumn = updatableColumns.length;

        const columnCopy = { ...stateObject.column };
        const componentCopy = { ...stateObject.component };
        const deletableColumn = columnCopy[columnId];
        const deletableComponentIds = deletableColumn.children;

        delete columnCopy[columnId];
        deletableComponentIds.forEach((compId) => {
          const decIds = deletableMedia(componentCopy[compId]);
          state.decrementIds.push(...decIds);
          delete componentCopy[compId];
        });

        sectionEntryCopy[sectionId] = {
          ...sectionEntry,
          children: updatableColumns,
        };

        updatableColumns.forEach((colId) => {

          columnCopy[colId] = {
            ...columnCopy[colId],
            style: {
              ...columnCopy[colId].style,
              width: { base: +(100 / numberOfColumn).toFixed(2) },
            },
          };
        });

        Object.assign(state, {
          section: { ids: state.section.ids, entries: sectionEntryCopy },
          column: columnCopy,
          component: componentCopy,
          selected: sectionId,
          selectedType: 'section',
        });
      }
    },


    // new component drop reducer
    dropComponent: {
      reducer: (state, action) => {
        const { result, columnId, component } = action.payload;
        let column = state.column[columnId].children;
        column.splice(result.destination.index, 0, component.id);
        state.component[component.id] = component;
        state.selected = component.id;
        state.selectedType = component.type;
        state.isPageUpdated = true;
      },
      prepare: ({ columnId, type, result }) => {
        const component = addComponent(type);
        return {
          payload: {
            component,
            columnId,
            result,
          },
        };
      },
    },



    formFieldReorder: (state, action) => {
      const { result } = action.payload;
      let fields = state.component[result.source.droppableId]['fields'];
      const [removedField] = fields.splice(result.source.index, 1);
      fields.splice(result.destination.index, 0, removedField);
      state.isPageUpdated = true;
    },

    selectComponent: (state, action) => {
      state.selected = action.payload.id;
      state.selectedType = action.payload.type;
    },

    selectSetting: (state, action) => {
      if (state.selectedType != 'builder-settings') {
        state.selected = '0000';
        state.selectedType = 'builder-settings';
      }
    },
    selectSeoSetting: (state, action) => {
      if (state.selectedType != 'seo-settings') {
        state.selected = '1111';
        state.selectedType = 'seo-settings';
      }
    },

    setBreakPointMode: (state, action) => {
      localStorage.setItem("viewport", action.payload.breakPoint);
      state.breakPointMode = action.payload;
    },

    deleteComponent: (state, action) => {
      state.column[action.payload.columnId].children.splice(
        action.payload.index,
        1
      );
      state.selected = null;
      state.selectedType = '';

      const decIds = deletableMedia(
        original(state.component[action.payload.id])
      );
      state.decrementIds.push(...decIds);
      delete state.component[action.payload.id];
      state.isPageUpdated = true;
    },

    updateComponent: (state, action) => {
      const { id, property, objectKey } = action.payload;
      state.isPageUpdated = true;
      if (objectKey in state.component[id] === false) {
        state.component[id][objectKey] = {};
      }
      if (property) {
        // If has property
        state.component[id][objectKey][property] = action.payload.value;
      } else {
        // If has no property
        state.component[id][objectKey] = action.payload.value;
      }
    },

    pasteComponentToColumn: (state, action) => {
      state.isPageUpdated = true;

      const comID = uuid();
      const component = JSON.parse(action.payload.component);
      component.id = comID;

      state.component[comID] = component;
      state.column[action.payload.column].children.push(comID);
      state.isPageUpdated = true;
      handleComponentPasteMedia(component, state.incrementIds)
    },

    pasteStyleToComponent: (state, action) => {
      state.isPageUpdated = true;

      if (
        action.payload.componentType ==
        state.component[action.payload.currentComponentId].type
      ) {
        const componentStyle = JSON.parse(action.payload.componentStyle);
        state.component[action.payload.currentComponentId].style =
          componentStyle;
      } else {
        alert('Can not paste here.');
      }
    },

    pasteAllComponentToColumn: (state, action) => {
      state.isPageUpdated = true;

      let { currentColumn, copyContent } = action.payload;
      let { columnChildren, columnComponent } = copyContent;

      const newChilds = columnChildren.map(() => {
        const comId = uuid();
        return comId;
      });

      const components = JSON.parse(columnComponent);

      columnChildren.map((child, index) => {
        let loopCom = components[child];
        state.component = {
          ...state.component,
          [newChilds[index]]: { ...loopCom, id: newChilds[index] },
        };
      });
      state.column[currentColumn].children = [
        ...state.column[currentColumn].children,
        ...newChilds,
      ];
      state.column[currentColumn].style = JSON.parse(copyContent.columnStyle)
      handleColumnPasteMedia(state.column[currentColumn], components, state.incrementIds)
    },

    updateFormField: (state, action) => {
      const { id, value, index } = action.payload;
      state.component[id].fields[index] = value;
      state.isPageUpdated = true;
    },

    addComponentItem: (state, action) => {
      state.isPageUpdated = true;

      state.component[action.payload.id][action.payload.itemKey].push(
        action.payload.item
      );
    },

    deleteComponentItem: (state, action) => {
      state.isPageUpdated = true;

      state.component[action.payload.id][action.payload.itemKey].splice(
        action.payload.index,
        1
      );
    },

    updateComponentItem: (state, action) => {
      state.isPageUpdated = true;

      const { id, itemKey, index, property, value } = action.payload;
      state.component[id][itemKey][index][property] = value;
    },

    updateInnerItemOfItem: (state, action) => {
      state.isPageUpdated = true;

      const { id, itemKey, index, property, innerIndex, innerProperty, value } =
        action.payload;
      const innerItems = state.component[id][itemKey][index][property];
      innerItems[innerIndex][innerProperty] = value;
    },

    increment: (state, action) => {
      state.incrementIds.push(action.payload.id);
    },

    decrement: (state, action) => {
      state.decrementIds.push(action.payload.id);
    },

    resetIncDec: (state) => {
      state.incrementIds = [];
      state.decrementIds = [];
    },

    setCopyableColumn: (state, action) => {
      state.copyableColumn = action.payload;
    },

    setWebpagePreview: (state, action) => {
      state.webPageBuilderPreview = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  updateState,
  updateName,
  addSection,
  selectSection,
  updateSection,
  addColumnToSection,
  deleteSection,
  reorderSection,
  selectSetting,
  selectSeoSetting,
  selectColumn,
  updateColumn,
  updateColumnWidth,
  deleteColumn,
  dropComponent,
  selectComponent,
  updateComponent,
  deleteComponent,
  addComponentItem,
  deleteComponentItem,
  updateComponentItem,
  updateInnerItemOfItem,
  removeSelection,
  increment,
  decrement,
  resetIncDec,
  reorderComponent,
  formFieldReorder,
  reorderComponentInAnyColumn,
  updateFormField,
  updateStateByTemplate,
  setCopyableColumn,
  setWebpagePreview,
  pasteComponentToColumn,
  pasteStyleToComponent,
  pasteAllComponentToColumn,
  updatePageSaveStatus,
  updateSelectedPageVersion,
  setBreakPointMode,
  reorderColumnInSectionSettings,
  dropComponentInEmptySection

} = pageBuilderSlice.actions;

export default pageBuilderSlice.reducer;
